import { Design, DesignType, Intent, IntentType } from '@Components/designConstants';

export interface ProductDoc {
  title: string;
  file: string;
  markdown: string;
}

export interface SlackChannel {
  name: string;
  id: string;
}

// Whether a product has real distilled documentation (`docs`, below), a raw source identified
// but not yet distilled into a reference note, or nothing identified at all -- see
// gen_product_roadmap_v2.py's SOURCE_IDENTIFIED for how "source-identified" gets assigned.
// Deliberately 3-state rather than a boolean: collapsing "not written yet" and "nothing to
// write from" into one "no docs" bucket would hide exactly the gap worth making visible.
export const DocsStatus = {
  HAS_REFERENCE: 'has-reference',
  SOURCE_IDENTIFIED: 'source-identified',
  NONE: 'none'
} as const;
export type DocsStatusType = (typeof DocsStatus)[keyof typeof DocsStatus];

export interface Product {
  name: string;
  kind: string;
  parent: string;
  scope: string;
  channel: string[];
  function: string[];
  audience: string;
  // `intelligence` facet (product-brain TAXONOMY.md §9): zero, one or both of
  // "Artificial intelligence" / "Machine learning". Optional because the generator only began
  // emitting it in product-brain #57 -- data published before that has no such key, and the page
  // must render it as "untagged" rather than crash.
  intelligence?: string[];
  desc: string;
  oneLiner: string;
  life: string;
  target: string;
  owner: string;
  docs: ProductDoc[];
  // Raw string, like `life`/`kind`/`scope`/`audience` above -- not the DocsStatusType union.
  // The fixture is a JSON import, which TS infers as plain `string` per field, so a literal-
  // union field type here would never structurally match it (this bit both, until narrowed).
  // Compare against the DocsStatus constants below rather than switching on a literal type.
  docsStatus: string;
  repoUrl: string;
  appPath: string[];
  slackChannel: SlackChannel | null;
  // Effort x Impact scores (product-brain ROADMAP.md, "Effort × Impact assessments"). Optional
  // because data published before product-brain #81 has neither key. `assessable` is false for
  // deployed and parked products -- they never appear on the 2x2; an assessable product with a
  // null `assessment` is "not yet scored", never zero.
  assessment?: ProductAssessment | null;
  assessable?: boolean;
}

export interface ProductAssessment {
  impact: number; // 1-10
  effort: number; // 1-10, work *remaining* to the next real release
  scoredBy: string;
  date: string;
  // True while the roadmap's "Scored by" says pending/draft -- drives the review banner.
  draft: boolean;
}

// One band of a scoring scale, as ROADMAP.md words it. `lead` is the part set in bold
// ("Strategic:", "2–6 weeks."); empty when the band has none.
export interface EffortImpactBand {
  band: string;
  lead: string;
  text: string;
}

export interface EffortImpactScale {
  definition: string;
  // A rule shown under the bands (Impact: binding commitments don't raise impact), or ''.
  note: string;
  bands: EffortImpactBand[]; // low band first
}

export interface EffortImpactMeta {
  inScope: number;
  scored: number;
  draft: boolean;
  // The "How scores work" card's text, published by the generator from ROADMAP.md
  // (product-brain #84). Absent from older data; the card is hidden without it.
  scales?: { impact: EffortImpactScale; effort: EffortImpactScale };
}

export interface ProductRoadmapData {
  meta: { asof: string; version: string; effortImpact?: EffortImpactMeta };
  products: Product[];
}

// The page's two tabs. The table keeps the bare /product-roadmap URL (it is the default tab, so
// existing links and bookmarks still land on it); the 2x2 lives at /product-roadmap/effort-impact.
export const RoadmapTab = {
  ROADMAP: 'roadmap',
  EFFORT_IMPACT: 'effort-impact'
} as const;
export type RoadmapTabType = (typeof RoadmapTab)[keyof typeof RoadmapTab];

export const ROADMAP_TABS: { key: RoadmapTabType; label: string }[] = [
  { key: RoadmapTab.ROADMAP, label: 'Roadmap' },
  { key: RoadmapTab.EFFORT_IMPACT, label: 'Effort × Impact' }
];

export const SortField = {
  NAME: 'name',
  PARENT: 'parent',
  DESCRIPTION: 'description',
  FUNCTION: 'function',
  STATUS: 'status',
  TARGET: 'target',
  OWNER: 'owner'
} as const;
export type SortFieldType = (typeof SortField)[keyof typeof SortField];

export const SortDirection = {
  ASC: 'asc',
  DESC: 'desc'
} as const;
export type SortDirectionType = (typeof SortDirection)[keyof typeof SortDirection];

export const FilterFacet = {
  PARENT: 'parent',
  FUNCTION: 'function',
  INTELLIGENCE: 'intelligence',
  STATUS: 'status'
} as const;
export type FilterFacetType = (typeof FilterFacet)[keyof typeof FilterFacet];

export const FILTER_FACETS: FilterFacetType[] = [
  FilterFacet.PARENT,
  FilterFacet.FUNCTION,
  FilterFacet.INTELLIGENCE,
  FilterFacet.STATUS
];

export const FILTER_FACET_LABEL: Record<FilterFacetType, string> = {
  [FilterFacet.PARENT]: 'Parent',
  [FilterFacet.FUNCTION]: 'Function',
  [FilterFacet.INTELLIGENCE]: 'Intelligence',
  [FilterFacet.STATUS]: 'Status'
};

// Canonical display order for `function` — matches PRODUCT-FACETS.md's own section order
// (inventory/PRODUCT-FACETS.md, "Organized by function (primary), the canonical grouping"),
// not alphabetical.
export const FUNCTION_ORDER: string[] = [
  'Measurement & Attribution',
  'Optimization',
  'Planning',
  'Buying & Activation',
  'Reporting',
  'Dashboarding',
  'Insights',
  'Data Connectors',
  'Automation',
  'QA & Reliability',
  'Identity',
  'Creative'
];

export interface StatusStyle {
  design?: DesignType;
  intent?: IntentType;
  // Not every bucket maps onto an existing Design/Intent token (this codebase has no "info"
  // blue) -- `custom` names a bucket-specific SCSS class for those; the class itself still
  // references a real colors.scss export (DivergentPositive400) rather than a hardcoded hex.
  custom?: 'progress' | 'alpha';
}

export const StatusBucket = {
  DEPLOYED: 'deployed',
  IN_PROGRESS: 'in_progress',
  IN_BUILD: 'in_build',
  ALPHA: 'alpha',
  BETA: 'beta',
  PLANNED: 'planned',
  HELD: 'held',
  UNKNOWN: 'unknown'
} as const;
export type StatusBucketType = (typeof StatusBucket)[keyof typeof StatusBucket];

export interface StatusBucketDef {
  key: StatusBucketType;
  label: string;
  match: RegExp;
  style: StatusStyle;
}

// The canonical 6-bucket taxonomy the retired HTML prototype's status strip showed (Deployed /
// In progress / Beta / In build / Planned / Paused-held) -- the roadmap fixture's raw `life`
// field carries ~12 finer-grained synonyms (e.g. Deployed/Live/Shipped/Shipping all mean "done"),
// which is fine as data but was being shown as 12 separate, uncollapsed filter chips. This list
// is the single source of truth for collapsing those synonyms back down to the 6 canonical
// buckets everywhere status is displayed (status bar, row accent bar).
export const STATUS_BUCKETS: StatusBucketDef[] = [
  // Alpha is the stage before Beta, so it sits beside it and borrows Beta's brand hue at a lighter
  // tint -- related, but distinguishable at a glance. Added 2026-10-08 when product-brain began
  // emitting an "Alpha" status; before that the label fell through to UNKNOWN.
  {
    key: StatusBucket.ALPHA,
    label: 'Alpha',
    match: /alpha/i,
    style: { custom: 'alpha' }
  },
  // BETA is checked before DEPLOYED: "partially shipped" would otherwise match DEPLOYED's
  // `shipped` term first (Array.find takes the first match), miscategorizing it as fully
  // deployed instead of beta.
  {
    key: StatusBucket.BETA,
    label: 'Beta',
    match: /beta|partially shipped/i,
    style: { design: Design.BRAND }
  },
  {
    key: StatusBucket.DEPLOYED,
    label: 'Deployed',
    match: /deployed|live|shipped|shipping/i,
    style: { intent: Intent.SUCCESS }
  },
  {
    key: StatusBucket.IN_PROGRESS,
    label: 'In progress',
    match: /in progress/i,
    style: { custom: 'progress' }
  },
  {
    key: StatusBucket.IN_BUILD,
    label: 'In build',
    match: /in build/i,
    style: { intent: Intent.WARNING }
  },
  {
    key: StatusBucket.PLANNED,
    label: 'Planned',
    match: /scoping|planned|backlog|unknown/i,
    style: { design: Design.NEUTRAL }
  },
  {
    key: StatusBucket.HELD,
    label: 'Paused/held',
    match: /paused|held back/i,
    style: { intent: Intent.ERROR }
  },
  // Catch-all, always last: any `life` value that reaches this point matched none of the
  // buckets above (e.g. a new value introduced on the backend). Kept distinct from HELD so an
  // unrecognized status doesn't silently render with HELD's alarming error/red styling, and kept
  // as a real STATUS_BUCKETS entry (rather than a separate fallback object) so it still shows up
  // as its own chip/count in distinctStatusBuckets, which filters against this array.
  {
    key: StatusBucket.UNKNOWN,
    label: 'Unknown',
    match: /.?/,
    style: { intent: Intent.INFO }
  }
];

export const getStatusBucket = (life: string): StatusBucketDef =>
  STATUS_BUCKETS.find(b => b.match.test(life)) ?? STATUS_BUCKETS[STATUS_BUCKETS.length - 1];

export const NAV_ID = 'product-roadmap';
export const PAGE_TITLE = 'Product Roadmap';

// The two `intelligence` values, spelled out. "Artificial intelligence" is deliberately not
// abbreviated to "AI": a text filter on those two letters also matches Explain, Retail, Domain.
export const Intelligence = {
  AI: 'Artificial intelligence',
  ML: 'Machine learning'
} as const;
